/**
 * Valuation History Page (/monitoring/valuation-history)
 *
 * Paginated, filterable list of Valuation records with a time-series chart.
 * Data source: GET /api/admin/collateral/valuations
 *
 * Renders:
 *   - The kit FilterBar, as on every other list page: position ID, security
 *     ID, From and To. A value is a draft in the filter menu until its Apply
 *     adds the chip, and only a chip change re-runs the query. The page kept
 *     a five-field form with its own Apply and Clear, which filled a 390px
 *     phone screen before any data (UI run r3, fi-cons-12). Page size sits with
 *     the pagination, which is what it controls.
 *   - The total market value of the current page at each valuation time, when
 *     the page holds two or more distinct times
 *   - Paginated data table of Valuation records
 *
 * Contract notes (schema-registry/platform-admin.openapi.yaml, ValuationQueryParams):
 * positionId and securityId are full UUIDs matched by equality, and from/to are
 * date-times. A calendar date is sent as the start and the end of that day in
 * the viewer's zone, so the To date includes the day it names.
 *
 * Critical path steps: 5 (System classifies assets), 10 (Aaim monitors collateral)
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  DataTable,
  Skeleton,
  Badge,
  Button,
  AreaChart,
  FilterBar,
  Select,
  formatDateTime,
  type ActiveFilter,
  type DataTableColumn,
  type FilterDefinition,
} from '@aaimplatform/ui';
import {
  collateralAnalyticsApi,
  type ValuationListResponse,
  type ValuationRecord,
  type ValuationQueryParams,
} from '../../api/services/collateral-analytics';
import { formatUsd, formatUsdCompact, labelFor, VALUATION_METHOD_LABELS } from './display-format';
import { readOutcome, refreshFailed } from './read-state';
import { ReadNotice } from './ReadNotice';

const SUBJECT = 'Valuation history';

/**
 * A valuation instant with its year and zone ('Jul 5, 2026, 11:10 PM PDT'),
 * the kit's one rule for instants. The local formatter dropped the zone (UI run
 * r3, fi-cons-3).
 */
function formatTimestamp(iso: string): string {
  return formatDateTime(iso, { seconds: false });
}

/** A chart axis label that tells two times on one day apart. */
function formatChartTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A calendar date from a date input as a date-time bound in the viewer's zone:
 * the first millisecond of the day for From, the last for To. A bare
 * 'YYYY-MM-DD' parsed by the server is midnight UTC, which dropped every
 * valuation on the To day itself.
 */
function dayBound(date: string, edge: 'start' | 'end'): string | undefined {
  if (!date) return undefined;
  const local = new Date(`${date}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}`);
  return Number.isNaN(local.getTime()) ? undefined : local.toISOString();
}

/**
 * The current page's total market value at each distinct valuation time.
 *
 * Each row is ONE position's valuation. Charting rows one to one joined
 * different positions into one line, so two positions valued at the same
 * moment read as one position falling from the first value to the second.
 */
function totalsByTimestamp(
  valuations: readonly ValuationRecord[],
): Array<{ timestamp: string; value: number }> {
  const totals = new Map<string, number>();
  for (const v of valuations) {
    const prior = totals.get(v.valuationTimestamp);
    totals.set(v.valuationTimestamp, prior === undefined ? v.marketValue : prior + v.marketValue);
  }
  return Array.from(totals.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([timestamp, value]) => ({ timestamp, value }));
}

const VALUATION_COLUMNS: DataTableColumn<ValuationRecord>[] = [
  {
    header: 'Timestamp',
    accessor: 'valuationTimestamp',
    align: 'left',
    cell: (value) => (
      <span className="font-mono text-sm whitespace-nowrap">{formatTimestamp(String(value))}</span>
    ),
  },
  {
    header: 'Position ID',
    accessor: 'positionId',
    align: 'left',
    // The full UUID: the Position ID filter matches it exactly, so a stub
    // gave the operator nothing to filter with.
    cell: (value) => (
      <span className="font-mono text-xs text-secondary break-all">{String(value)}</span>
    ),
  },
  {
    header: 'Quantity',
    accessor: 'quantity',
    align: 'right',
    monospace: true,
    cell: (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 }),
  },
  {
    header: 'Market Price',
    accessor: 'marketPrice',
    align: 'right',
    monospace: true,
    cell: (value) => formatUsd(Number(value)),
  },
  {
    header: 'Market Value',
    accessor: 'marketValue',
    align: 'right',
    monospace: true,
    cell: (value) => formatUsd(Number(value)),
  },
  {
    header: 'Eligible Value',
    accessor: 'eligibleValue',
    align: 'right',
    monospace: true,
    cell: (value) => formatUsd(Number(value)),
  },
  {
    header: 'Source',
    accessor: 'priceSource',
    align: 'left',
    cell: (value) => <Badge variant="neutral">{labelFor(String(value))}</Badge>,
  },
  {
    header: 'Method',
    accessor: 'valuationMethod',
    align: 'left',
    cell: (value) => (
      <span className="font-sans text-xs text-secondary">
        {labelFor(String(value), VALUATION_METHOD_LABELS)}
      </span>
    ),
  },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100].map((n) => ({ value: String(n), label: `${n} per page` }));

const FILTERS: FilterDefinition[] = [
  { id: 'positionId', label: 'Position ID', type: 'text', placeholder: 'Full position UUID' },
  { id: 'securityId', label: 'Security ID', type: 'text', placeholder: 'Full security UUID' },
  { id: 'from', label: 'From', type: 'date' },
  { id: 'to', label: 'To', type: 'date' },
];

/** The ID filters and the message for a value that is not a full UUID. */
const ID_FILTER_ERRORS: Readonly<Record<string, string>> = {
  positionId: 'Enter the full position UUID.',
  securityId: 'Enter the full security UUID.',
};

/** The applied value of one filter, or '' when it is not applied. */
function appliedValue(filters: readonly ActiveFilter[], id: string): string {
  return filters.find((f) => f.filterId === id)?.value ?? '';
}

export function ValuationHistoryPage() {
  // The FilterBar chips are the applied filters and key the query. A value is
  // a draft in the filter menu until its Apply adds the chip, so the query does
  // not re-run per keystroke. An ID that is not a full UUID is refused and
  // named rather than sent, since the server matches the ID exactly.
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const queryParams: ValuationQueryParams = {
    positionId: appliedValue(activeFilters, 'positionId') || undefined,
    securityId: appliedValue(activeFilters, 'securityId') || undefined,
    from: dayBound(appliedValue(activeFilters, 'from'), 'start'),
    to: dayBound(appliedValue(activeFilters, 'to'), 'end'),
    page,
    limit,
  };

  const query = useQuery({
    queryKey: ['admin', 'collateral', 'valuations', queryParams],
    queryFn: () => collateralAnalyticsApi.listValuations(queryParams),
    placeholderData: (prev: ValuationListResponse | undefined) => prev,
  });
  const outcome = readOutcome(query);

  const handleFiltersChange = useCallback((next: ActiveFilter[]) => {
    const refused = next.find(
      (f) => f.filterId in ID_FILTER_ERRORS && !UUID_PATTERN.test(f.value.trim()),
    );
    if (refused) {
      setFilterError(ID_FILTER_ERRORS[refused.filterId] ?? null);
      return;
    }
    setFilterError(null);
    setActiveFilters(next.map((f) => ({ ...f, value: f.value.trim() })));
    setPage(1);
  }, []);

  const data = outcome === 'read' ? query.data : undefined;
  const valuations = data?.valuations ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  const totals = totalsByTimestamp(valuations);
  const chartData = totals.map((t) => ({ time: formatChartTime(t.timestamp), value: t.value }));

  const countText =
    outcome === 'loading'
      ? 'Loading...'
      : outcome === 'failed'
        ? `${SUBJECT} could not be read.`
        : data
          ? `${data.total.toLocaleString('en-US')} total valuations`
          : `${SUBJECT} not yet read.`;

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="max-w-content-narrow text-sm font-sans text-tertiary">
          Per-position valuation records with market price, market value, and eligible
          value. Filter by position, security, or date range.
        </p>
      </div>

      <ReadNotice
        subject={SUBJECT}
        failed={outcome === 'failed'}
        refreshFailed={refreshFailed(query)}
        dataUpdatedAt={query.dataUpdatedAt}
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />

      <div className="mb-6">
        <FilterBar
          filters={FILTERS}
          activeFilters={activeFilters}
          onFiltersChange={handleFiltersChange}
          className="rounded-sm"
        />
        {filterError && (
          <p role="alert" className="mt-2 text-xs font-sans text-error">
            {filterError}
          </p>
        )}
      </div>

      {/* Time-series chart: the page's total at each distinct valuation time. */}
      {outcome === 'loading' ? (
        <div className="mb-8">
          <Card>
            <Skeleton className="h-56" />
          </Card>
        </div>
      ) : chartData.length > 1 ? (
        <div className="mb-8">
          <AreaChart
            title="Total Market Value by Valuation Time (Current Page)"
            data={chartData}
            dataKey="value"
            xAxisKey="time"
            color="rgb(var(--color-alternative))"
            formatValue={formatUsdCompact}
            height={240}
          />
        </div>
      ) : chartData.length === 1 ? (
        <p className="mb-6 text-xs font-sans text-tertiary">
          Every valuation on this page shares one valuation time, so there is no
          trend to draw.
        </p>
      ) : null}

      {/* Pagination info + table */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-label-xs font-sans text-tertiary">{countText}</span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-36">
              <Select
                id="valuation-page-size"
                aria-label="Page size"
                options={PAGE_SIZE_OPTIONS}
                value={String(limit)}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              />
            </div>
            {data && totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-secondary">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>

        {outcome === 'loading' ? (
          <Skeleton className="h-64" />
        ) : valuations.length > 0 ? (
          <DataTable columns={VALUATION_COLUMNS} data={valuations} />
        ) : data ? (
          <Card>
            <div className="flex items-center justify-center h-24 text-secondary font-sans text-sm">
              No valuation records match the current filter.
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
